import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { AlertTriangle, ArrowLeft, CheckCircle2, Loader2, Lock } from "lucide-react";
import { toast } from "sonner";
import { useT } from "@/i18n";
import { useAuth } from "@/context/AuthContext";
import { useData } from "@/context/DataContext";
import { Button, Card, SectionHeading, Stepper } from "@/components/ui";
import { CertificateTypeIcon } from "@/components/CertificateTypeIcon";
import { UploadDropzone, type PickedFile } from "@/components/UploadDropzone";
import {
  ApiError,
  certificateRequestsApi,
  paymentsApi,
  requestDraftApi,
  type CustomerDocType,
  type DocValidation,
} from "@/lib/api";
import { NequiPhoneDialog } from "@/components/NequiPhoneDialog";
import {
  DEFAULT_VALIDITY_MONTHS,
  formatCop,
  isCertificateType,
  isCertificateTypeTransactional,
  listedPriceForCertificate,
  listedValidityOptionsFor,
  priceForCertificate,
  validityOptionsFor,
  type CertificateType,
  type ValidityMonths,
} from "@pahcertificar/certmanager-payment-gateway/pricing";
import { departments, municipalitiesOf, canonicalLocation } from "@/lib/locations";
import { cn } from "@/lib/cn";
import { composeDisplayName } from "@/lib/person-name";
import { PersonNameFields } from "@/components/PersonNameFields";

// Catalog prices and direct-checkout availability come from the shared
// payment-gateway table. Token products are selected here, then continued in
// the secure purchase channel presented by the app.
const VALIDITY_LABEL_KEYS = {
  3: "req.validity_m3",
  6: "req.validity_m6",
  12: "req.validity_1",
  24: "req.validity_2",
} as const satisfies Record<ValidityMonths, string>;

type FormState = {
  type: CertificateType | null;
  name: string;
  firstName: string;
  secondName: string;
  firstSurname: string;
  secondSurname: string;
  docType: "CC" | "CE" | "PA" | "NIT";
  docNumber: string;
  email: string;
  phone: string;
  city: string;
  department: string;
  address: string;
  validity: ValidityMonths;
  docs: {
    cedula: PickedFile | null;
    rut: PickedFile | null;
    camara: PickedFile | null;
  };
};

// Wizard doc slots ↔ the server artifact type they upload as.
const DOC_SLOTS = [
  { key: "cedula", type: "cedula" },
  { key: "rut", type: "rut" },
  { key: "camara", type: "camara_comercio" },
] as const satisfies ReadonlyArray<{ key: keyof FormState["docs"]; type: CustomerDocType }>;

// Facturación leads the catalog. The eight token products follow in the exact
// customer-facing order used by the corporate site.
const CERTIFICATE_TYPES: { id: CertificateType; audience: "natural" | "juridica" | "mixed"; tagKey: string }[] = [
  { id: "facturacion", audience: "mixed", tagKey: "DIAN" },
  { id: "firma_natural", audience: "natural", tagKey: "Persona Natural" },
  { id: "firma_natural_rut", audience: "natural", tagKey: "Persona Natural con RUT" },
  { id: "firma_representante", audience: "juridica", tagKey: "Representante Legal" },
  { id: "profesional", audience: "natural", tagKey: "Profesional Titulado" },
  { id: "firma_juridica", audience: "juridica", tagKey: "Pertenencia a Empresa" },
  { id: "funcion_publica", audience: "natural", tagKey: "Función Pública" },
  { id: "funcion_publica_siif", audience: "natural", tagKey: "SIIF Nación" },
  { id: "comunidad_academica", audience: "mixed", tagKey: "Comunidad Académica" },
];

const TOKEN_ISSUER_ECOMMERCE_URL =
  "https://ecommerce.andesscd.com.co/auth/login?returnUrl=%2Fshop";

/** Clamp persisted/query data to a period publicly listed for the product. */
function clampValidity(type: CertificateType | null, months: number | undefined): ValidityMonths {
  if (!type) {
    return months === 3 || months === 6 || months === 12 || months === 24
      ? months
      : DEFAULT_VALIDITY_MONTHS;
  }
  const options = listedValidityOptionsFor(type);
  return options.includes(months as ValidityMonths)
    ? (months as ValidityMonths)
    : DEFAULT_VALIDITY_MONTHS;
}

function generateRef() {
  const chars = "ABCDEFGHJKLMNPQRSTUVWXYZ23456789";
  let out = "";
  for (let i = 0; i < 8; i++) out += chars[Math.floor(Math.random() * chars.length)];
  return `PC-${out}`;
}

export function Request() {
  const { t } = useT();
  const auth = useAuth();
  const { data } = useData();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const phone = auth.status === "authenticated" ? auth.user.phone : "";
  const requestedTypeValue = searchParams.get("tipo");
  const requestedType = isCertificateType(requestedTypeValue)
    ? requestedTypeValue
    : null;
  const requestedValidityValue = Number(searchParams.get("vigencia"));
  const requestedValidity = [3, 6, 12, 24].includes(requestedValidityValue)
    ? (requestedValidityValue as ValidityMonths)
    : undefined;

  const [step, setStep] = useState(0);
  const [submittedRef, setSubmittedRef] = useState<string | null>(null);
  const [paying, setPaying] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogError, setDialogError] = useState<{ code: string; message: string } | null>(null);
  const [form, setForm] = useState<FormState>(() => {
    const location = canonicalLocation(data.profile.department, data.profile.city);
    return {
      type: requestedType,
      name: data.profile.companyName ?? composeDisplayName(data.profile),
      firstName: data.profile.firstName,
      secondName: data.profile.secondName,
      firstSurname: data.profile.firstSurname,
      secondSurname: data.profile.secondSurname,
      docType: data.profile.documentType,
      docNumber: data.profile.documentNumber,
      email: data.profile.email,
      phone: phone ? `+${phone}` : "",
      city: location.city,
      department: location.department,
      address: data.profile.address,
      validity: clampValidity(requestedType, requestedValidity),
      docs: { cedula: null, rut: null, camara: null },
    };
  });

  // Server-side draft state (PC-27): the request the wizard's docs upload
  // against (created lazily) and which docs are already on the server.
  const requestIdRef = useRef<string | null>(null);
  const creatingRef = useRef<Promise<string> | null>(null);
  const [requestId, setRequestId] = useState<string | null>(null);
  const [uploadedDocs, setUploadedDocs] = useState<Partial<Record<CustomerDocType, string>>>({});
  // AI document-validation (PC-29): per-slot verdict + in-flight flag. Advisory.
  const [validations, setValidations] = useState<Partial<Record<CustomerDocType, DocValidation | null>>>({});
  const [validatingDocs, setValidatingDocs] = useState<Partial<Record<CustomerDocType, boolean>>>({});
  const [hydrated, setHydrated] = useState(false);

  const isJuridica = form.docType === "NIT";
  const selectedTokenType =
    form.type && !isCertificateTypeTransactional(form.type) ? form.type : null;
  // Live price for the selected (type, validity) — the same lookup the API
  // uses to charge, so the card, the footer and the Nequi push always agree.
  const priceCop =
    form.type && isCertificateTypeTransactional(form.type)
      ? priceForCertificate(form.type, form.validity).cop
      : null;
  const totalSteps = 4;
  const steps = [
    { id: 0, label: t("req.step1") },
    { id: 1, label: t("req.step2") },
    { id: 2, label: t("req.step3") },
    { id: 3, label: t("req.step4") },
  ];

  // ── Resume: load the server-side draft on mount ──────────────────────────
  useEffect(() => {
    let cancelled = false;
    requestDraftApi
      .get()
      .then((res) => {
        if (cancelled) return;
        const d = res.draft;
        if (d) {
          // Drafts saved before PC-35 may hold a retired catalog type (reset
          // to reselect) and stored the validity in years — map 1/2/3 años to
          // the closest purchasable period in months.
          const knownDraftType = d.type && isCertificateType(d.type) ? d.type : null;
          const draftType = knownDraftType;
          const preferredType = requestedType ?? draftType;
          const legacyMonths =
            d.validity === 1 ? 12 : d.validity === 2 || d.validity === 3 ? 24 : undefined;
          setForm((f) => ({
            ...f,
            type: preferredType ?? f.type,
            name: d.name ?? f.name,
            firstName: d.firstName ?? f.firstName,
            secondName: d.secondName ?? f.secondName,
            firstSurname: d.firstSurname ?? f.firstSurname,
            secondSurname: d.secondSurname ?? f.secondSurname,
            docType: d.docType ?? f.docType,
            docNumber: d.docNumber ?? f.docNumber,
            email: d.email ?? f.email,
            phone: d.phone ?? f.phone,
            city: d.city ?? f.city,
            department: d.department ?? f.department,
            address: d.address ?? f.address,
            validity: clampValidity(
              preferredType,
              requestedValidity ?? d.validityMonths ?? legacyMonths ?? f.validity
            ),
          }));
          if (typeof d.step === "number") {
            // A draft whose type was retired restarts at the type step.
            const resumeStep = requestedType ? 0 : d.type && !draftType ? 0 : d.step;
            setStep(Math.min(Math.max(resumeStep, 0), totalSteps - 1));
          }
          if (d.requestId) {
            requestIdRef.current = d.requestId;
            setRequestId(d.requestId);
          }
          if (d.uploaded) {
            const map: Partial<Record<CustomerDocType, string>> = {};
            for (const u of d.uploaded) map[u.type] = u.name;
            setUploadedDocs(map);
          }
        }
      })
      .catch(() => {
        // No draft / offline → start fresh.
      })
      .finally(() => {
        if (!cancelled) setHydrated(true);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Autosave the draft (debounced) once hydrated ─────────────────────────
  useEffect(() => {
    if (!hydrated) return;
    if (!form.type && step === 0) return; // nothing meaningful to persist yet
    const payload = {
      step,
      type: form.type,
      name: form.name,
      firstName: form.firstName,
      secondName: form.secondName,
      firstSurname: form.firstSurname,
      secondSurname: form.secondSurname,
      docType: form.docType,
      docNumber: form.docNumber,
      email: form.email,
      phone: form.phone,
      city: form.city,
      department: form.department,
      address: form.address,
      validityMonths: form.validity,
      requestId: requestIdRef.current,
      uploaded: Object.entries(uploadedDocs).map(([type, name]) => ({
        type: type as CustomerDocType,
        name: name as string,
      })),
    };
    const id = setTimeout(() => {
      requestDraftApi.save(payload).catch(() => {});
    }, 600);
    return () => clearTimeout(id);
  }, [hydrated, step, form, uploadedDocs, requestId]);

  const canGoNext = useMemo(() => {
    if (step === 0)
      return !!form.type && isCertificateTypeTransactional(form.type);
    if (step === 1)
      return (
        (isJuridica ? !!form.name : !!form.firstName && !!form.firstSurname) &&
        form.docNumber &&
        form.email &&
        form.phone &&
        form.city &&
        form.department &&
        form.address
      );
    if (step === 2) {
      // Cédula is always required. Persona natural may skip the RUT; persona
      // jurídica still needs the RUT and the Cámara de Comercio. A doc counts as
      // present if it's picked this session OR already uploaded to the server.
      const has = (key: keyof FormState["docs"], type: CustomerDocType) =>
        !!form.docs[key] || !!uploadedDocs[type];
      if (!has("cedula", "cedula")) return false;
      if (isJuridica) return has("rut", "rut") && has("camara", "camara_comercio");
      return true;
    }
    return true;
  }, [step, form, isJuridica, uploadedDocs]);

  if (submittedRef) {
    return <Success reference={submittedRef} email={form.email} />;
  }

  // Create the pending request once (idempotent within the wizard) so docs can
  // upload to the server immediately and the process is resumable.
  const ensureRequest = async (): Promise<string> => {
    if (requestIdRef.current) return requestIdRef.current;
    if (creatingRef.current) return creatingRef.current;
    const certificateType = form.type;
    if (!certificateType || !isCertificateTypeTransactional(certificateType)) {
      throw new Error("Este tipo no está disponible para solicitud en línea");
    }
    const p = (async () => {
      const created = await certificateRequestsApi.create({
        certificateType,
        validityMonths: form.validity,
        address: form.address,
        department: form.department,
        city: form.city,
        ...(form.docType === "NIT"
          ? {}
          : {
              firstName: form.firstName,
              secondName: form.secondName || null,
              firstSurname: form.firstSurname,
              secondSurname: form.secondSurname || null,
            }),
      });
      requestIdRef.current = created.request.id;
      setRequestId(created.request.id);
      return created.request.id;
    })();
    creatingRef.current = p;
    try {
      return await p;
    } finally {
      creatingRef.current = null;
    }
  };

  // Pick (or clear) a supporting document. Picking uploads it to R2 right away.
  const handlePickDoc = async (
    key: keyof FormState["docs"],
    type: CustomerDocType,
    picked: PickedFile | null
  ) => {
    setForm((f) => ({ ...f, docs: { ...f.docs, [key]: picked } }));
    if (!picked) {
      setUploadedDocs((u) => {
        const next = { ...u };
        delete next[type];
        return next;
      });
      setValidations((v) => {
        const next = { ...v };
        delete next[type];
        return next;
      });
      return;
    }
    // Clear any previous verdict and show the validating spinner.
    setValidations((v) => {
      const next = { ...v };
      delete next[type];
      return next;
    });
    setValidatingDocs((v) => ({ ...v, [type]: true }));
    try {
      const id = await ensureRequest();
      const res = await certificateRequestsApi.uploadDocument(id, picked.file, type, picked.name);
      setUploadedDocs((u) => ({ ...u, [type]: picked.name }));
      // Advisory verdict — surfaced inline, never blocks. null = AI skipped.
      setValidations((v) => ({ ...v, [type]: res.validation ?? null }));
    } catch (err) {
      toast.error(
        err instanceof ApiError ? err.message : "No pudimos subir el documento. Reintenta."
      );
    } finally {
      setValidatingDocs((v) => ({ ...v, [type]: false }));
    }
  };

  const handleNext = () => {
    if (!canGoNext) {
      toast.error("Completa los campos requeridos");
      return;
    }
    setStep((s) => Math.min(s + 1, totalSteps - 1));
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleBack = () => {
    setStep((s) => Math.max(s - 1, 0));
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleSubmit = () => {
    if (!form.type || !isCertificateTypeTransactional(form.type) || paying) return;
    setDialogError(null);
    setDialogOpen(true);
  };

  const handleConfirmPay = async (nequiPhone: string) => {
    const certificateType = form.type;
    if (!certificateType || !isCertificateTypeTransactional(certificateType) || paying) return;
    const ref = generateRef();
    setPaying(true);
    setDialogError(null);
    try {
      // The request + its documents already live on the server (created/uploaded
      // during the wizard). Make sure the request exists and flush any document
      // that failed to upload earlier, then start the checkout.
      const id = await ensureRequest();
      for (const slot of DOC_SLOTS) {
        const picked = form.docs[slot.key];
        if (picked && !uploadedDocs[slot.type]) {
          await certificateRequestsApi.uploadDocument(id, picked.file, slot.type, picked.name);
          setUploadedDocs((u) => ({ ...u, [slot.type]: picked.name }));
        }
      }
      const checkout = await paymentsApi.createCheckout({
        certificateType,
        validityMonths: form.validity,
        nequiPhone,
        certificateRequestId: id,
      });
      // The request is now a real pending request tied to the payment — drop the
      // draft so the wizard starts clean next time.
      requestDraftApi.clear().catch(() => {});
      if (checkout.flow === "redirect" && checkout.checkoutUrl) {
        window.location.assign(checkout.checkoutUrl);
      } else {
        navigate(`/pagar/${checkout.token}`, {
          state: { nequiPhone, ref, amountCop: checkout.amountCop },
        });
      }
    } catch (err) {
      // 422 NEQUI_PAYER_NOT_FOUND / NEQUI_REJECTED → keep the dialog open
      // with inline retry; everything else falls back to a toast.
      if (
        err instanceof ApiError &&
        err.status === 422 &&
        err.code &&
        err.code.startsWith("NEQUI_")
      ) {
        setDialogError({ code: err.code, message: err.message });
      } else {
        const message =
          err instanceof ApiError
            ? err.message
            : err instanceof Error
            ? err.message
            : "No pudimos iniciar el pago. Intenta de nuevo.";
        toast.error(message);
        setDialogOpen(false);
      }
      setPaying(false);
    }
  };

  return (
    <div className="space-y-8">
      <button
        type="button"
        onClick={() => navigate(-1)}
        className="inline-flex items-center gap-1.5 text-sm font-medium text-slate-500 hover:text-slate-900"
      >
        <ArrowLeft className="h-4 w-4" />
        {t("common.back")}
      </button>

      <SectionHeading
        title={t("req.title")}
        subtitle={t("req.progress", { current: step + 1, total: totalSteps })}
      />

      <Card className="p-4 sm:p-6">
        <Stepper steps={steps} current={step} />
      </Card>

      {step === 0 && <StepType form={form} setForm={setForm} />}
      {step === 1 && <StepData form={form} setForm={setForm} />}
      {step === 2 && (
        <StepDocs
          form={form}
          isJuridica={isJuridica}
          uploadedDocs={uploadedDocs}
          validations={validations}
          validatingDocs={validatingDocs}
          onPick={handlePickDoc}
        />
      )}
      {step === 3 && <StepReview form={form} isJuridica={isJuridica} uploadedDocs={uploadedDocs} />}

      {!(step === 0 && selectedTokenType) && (
        <div className="sticky bottom-[65px] z-20 -mx-4 flex items-center justify-between gap-3 border-t border-slate-200 bg-white/90 px-4 py-3 backdrop-blur sm:-mx-6 sm:px-6 lg:bottom-0 lg:-mx-8 lg:px-8">
          <Button variant="ghost" onClick={handleBack} disabled={step === 0}>
            {t("common.back")}
          </Button>
          {step < totalSteps - 1 ? (
            <Button onClick={handleNext} disabled={!canGoNext}>
              {t("common.continue")}
            </Button>
          ) : (
            <div className="flex flex-col items-end gap-1 sm:flex-row sm:items-center sm:gap-3">
              <span className="text-xs text-slate-500">
                {t("req.total")}:{" "}
                <span className="font-semibold text-slate-900">
                  {priceCop !== null ? formatCop(priceCop) : "—"}
                </span>
              </span>
              <Button onClick={handleSubmit} size="lg" loading={paying && !dialogOpen}>
                <Lock className="h-4 w-4" />
                {t("req.pay_and_submit")}
              </Button>
            </div>
          )}
        </div>
      )}

      <NequiPhoneDialog
        open={dialogOpen}
        defaultPhone={phone || form.phone}
        amountCop={priceCop ?? 0}
        loading={paying}
        errorCode={dialogError?.code}
        errorMessage={dialogError?.message}
        onCancel={() => {
          if (paying) return;
          setDialogOpen(false);
          setDialogError(null);
        }}
        onConfirm={handleConfirmPay}
      />
    </div>
  );
}

function StepType({ form, setForm }: { form: FormState; setForm: (f: FormState) => void }) {
  const { t } = useT();
  const selectedToken =
    form.type && !isCertificateTypeTransactional(form.type) ? form.type : null;
  const selectedTokenOptions = selectedToken
    ? listedValidityOptionsFor(selectedToken)
    : [];
  const facturacion = CERTIFICATE_TYPES[0];
  const tokenProducts = CERTIFICATE_TYPES.slice(1);

  const typeCard = (
    ct: (typeof CERTIFICATE_TYPES)[number],
    featured = false
  ) => {
    const active = form.type === ct.id;
    const available = isCertificateTypeTransactional(ct.id);
    const options = available
      ? validityOptionsFor(ct.id)
      : listedValidityOptionsFor(ct.id);
    const fromCop =
      options[0] !== undefined
        ? available
          ? priceForCertificate(ct.id, options[0]).cop
          : listedPriceForCertificate(ct.id, options[0]).cop
        : null;

    return (
      <button
        key={ct.id}
        type="button"
        onClick={() =>
          setForm({ ...form, type: ct.id, validity: clampValidity(ct.id, form.validity) })
        }
        className={cn(
          "flex w-full items-start gap-4 rounded-2xl border text-left transition",
          featured ? "p-5 sm:p-6" : "p-4",
          featured
            ? active
              ? "border-emerald-500 bg-emerald-50 shadow-pop"
              : "border-emerald-200 bg-gradient-to-br from-emerald-50 to-white hover:-translate-y-0.5 hover:shadow-card"
            : active
              ? "border-brand-600 bg-brand-50/60 shadow-pop"
              : "border-slate-200 bg-white hover:-translate-y-0.5 hover:shadow-card"
        )}
        aria-pressed={active}
      >
        <CertificateTypeIcon type={ct.id} size="md" />
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <p className={cn("font-semibold text-slate-900", featured && "text-lg")}>
              {t(`type.${ct.id}` as const)}
            </p>
            {active && (
              <CheckCircle2
                className={cn("h-4 w-4", featured ? "text-emerald-600" : "text-brand-600")}
              />
            )}
          </div>
          <p className="mt-1 text-sm text-slate-600">{t(`type.${ct.id}_desc` as const)}</p>
          <div className="mt-3 flex flex-wrap items-center gap-2">
            <span className="inline-flex rounded-full bg-slate-100 px-2.5 py-1 text-[11px] font-medium text-slate-700">
              {ct.tagKey}
            </span>
            <span
              className={cn(
                "inline-flex rounded-full px-2.5 py-1 text-[11px] font-semibold",
                available
                  ? "bg-emerald-100 text-emerald-800"
                  : "bg-brand-50 text-brand-700"
              )}
            >
              {available && fromCop !== null
                ? `${t("req.available_online")} · ${formatCop(fromCop)}`
                : fromCop !== null
                  ? `${t("req.token_available")} · ${t("req.price_from", {
                      price: formatCop(fromCop),
                    })}`
                  : t("req.token_available")}
            </span>
          </div>
        </div>
      </button>
    );
  };

  return (
    <div className="space-y-4">
      <header>
        <h2 className="text-2xl font-semibold">{t("req.step1_title")}</h2>
        <p className="mt-1 text-sm text-slate-600">{t("req.step1_subtitle")}</p>
      </header>
      {facturacion && typeCard(facturacion, true)}
      {selectedToken && (
        <Card className="border-brand-200 bg-gradient-to-br from-brand-50 to-white p-6">
          <div className="flex items-start gap-4">
            <CertificateTypeIcon type={selectedToken} size="md" />
            <div>
              <h3 className="font-semibold text-slate-900">
                {t(`type.${selectedToken}` as const)}
              </h3>
              <p className="mt-1 text-sm leading-relaxed text-slate-600">
                {t("req.token_note")}
              </p>
            </div>
          </div>
          <div className="mt-5 grid grid-cols-2 gap-2 sm:grid-cols-4">
            {selectedTokenOptions.map((validity) => (
              <button
                key={validity}
                type="button"
                onClick={() => setForm({ ...form, validity })}
                className={cn(
                  "rounded-xl border px-3 py-3 text-center transition",
                  form.validity === validity
                    ? "border-brand-600 bg-white shadow-sm"
                    : "border-brand-100 bg-white/70 hover:border-brand-300"
                )}
                aria-pressed={form.validity === validity}
              >
                <span className="block text-xs font-medium text-slate-600">
                  {t(VALIDITY_LABEL_KEYS[validity])}
                </span>
                <span className="mt-1 block text-sm font-semibold text-slate-900">
                  {formatCop(
                    listedPriceForCertificate(selectedToken, validity).cop
                  )}
                </span>
              </button>
            ))}
          </div>
          <a
            href={TOKEN_ISSUER_ECOMMERCE_URL}
            className="btn-primary mt-5 inline-flex"
          >
            {t("req.token_cta")}
          </a>
        </Card>
      )}
      <section className="space-y-3 pt-2" aria-labelledby="token-catalog-title">
        <div>
          <h3 id="token-catalog-title" className="text-lg font-semibold text-slate-900">
            {t("req.token_catalog_title")}
          </h3>
          <p className="mt-1 text-sm text-slate-600">{t("req.token_catalog_subtitle")}</p>
        </div>
        <div className="grid gap-3 md:grid-cols-2">
          {tokenProducts.map((ct) => typeCard(ct))}
        </div>
      </section>
    </div>
  );
}

function StepData({ form, setForm }: { form: FormState; setForm: (f: FormState) => void }) {
  const { t } = useT();
  const update = (patch: Partial<FormState>) => setForm({ ...form, ...patch });

  // Department drives the city options. We store the canonical DANE names
  // (not codes) to stay consistent with the profile model and review screen.
  const cityOptions = useMemo(() => municipalitiesOf(form.department), [form.department]);

  return (
    <div className="space-y-4">
      <header>
        <h2 className="text-2xl font-semibold">{t("req.step2_title")}</h2>
        <p className="mt-1 text-sm text-slate-600">{t("req.step2_subtitle")}</p>
      </header>
      <Card className="space-y-5 p-6">
        {form.docType === "NIT" ? (
          <Field label={t("name.company")} required>
            <input className="input" value={form.name} onChange={(e) => update({ name: e.target.value })} />
          </Field>
        ) : (
          <PersonNameFields
            value={{
              firstName: form.firstName,
              secondName: form.secondName,
              firstSurname: form.firstSurname,
              secondSurname: form.secondSurname,
            }}
            onChange={(next) => update(next)}
          />
        )}
        <div className="grid gap-4 sm:grid-cols-[200px_1fr]">
          <Field label={t("req.field_doc_type")} required>
            <select
              className="input"
              value={form.docType}
              onChange={(e) => update({ docType: e.target.value as FormState["docType"] })}
            >
              <option value="CC">Cédula de ciudadanía</option>
              <option value="CE">Cédula de extranjería</option>
              <option value="PA">Pasaporte</option>
              <option value="NIT">NIT</option>
            </select>
          </Field>
          <Field label={t("req.field_doc_number")} required>
            <input
              className="input"
              value={form.docNumber}
              onChange={(e) => update({ docNumber: e.target.value })}
              inputMode="numeric"
            />
          </Field>
        </div>
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label={t("req.field_email")} required>
            <input
              className="input"
              type="email"
              value={form.email}
              onChange={(e) => update({ email: e.target.value })}
            />
          </Field>
          <Field label={t("req.field_phone")} required>
            <input
              className="input"
              type="tel"
              value={form.phone}
              onChange={(e) => update({ phone: e.target.value })}
            />
          </Field>
        </div>
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label={t("req.field_department")} required>
            <select
              className="input"
              value={form.department}
              onChange={(e) => update({ department: e.target.value, city: "" })}
            >
              <option value="">{t("req.select_department")}</option>
              {departments.map((d) => (
                <option key={d.code} value={d.name}>
                  {d.name}
                </option>
              ))}
            </select>
          </Field>
          <Field label={t("req.field_city")} required>
            <select
              className="input"
              value={form.city}
              disabled={!form.department}
              onChange={(e) => update({ city: e.target.value })}
            >
              <option value="">
                {form.department ? t("req.select_city") : t("req.select_city_first")}
              </option>
              {cityOptions.map((m) => (
                <option key={m.code} value={m.name}>
                  {m.name}
                </option>
              ))}
            </select>
          </Field>
        </div>
        <Field label={t("req.field_address")} required>
          <input className="input" value={form.address} onChange={(e) => update({ address: e.target.value })} />
        </Field>

        <Field label={t("req.field_validity")}>
          {(() => {
            // Periods and prices come from the shared transactional table.
            const options = form.type
              ? validityOptionsFor(form.type)
              : [DEFAULT_VALIDITY_MONTHS];
            return (
              <>
                <div
                  className={cn(
                    "grid gap-2",
                    options.length > 1 ? "grid-cols-2 sm:grid-cols-4" : "grid-cols-1"
                  )}
                >
                  {options.map((v) => (
                    <button
                      key={v}
                      type="button"
                      onClick={() => update({ validity: v })}
                      className={cn(
                        "rounded-xl border px-3 py-2.5 text-center transition",
                        form.validity === v
                          ? "border-brand-600 bg-brand-50"
                          : "border-slate-200 bg-white hover:bg-slate-50"
                      )}
                      aria-pressed={form.validity === v}
                    >
                      <span
                        className={cn(
                          "block text-sm font-medium",
                          form.validity === v ? "text-brand-700" : "text-slate-700"
                        )}
                      >
                        {t(VALIDITY_LABEL_KEYS[v])}
                      </span>
                      {form.type && (
                        <span
                          className={cn(
                            "mt-0.5 block text-xs",
                            form.validity === v ? "text-brand-600" : "text-slate-500"
                          )}
                        >
                          {formatCop(priceForCertificate(form.type, v).cop)}
                        </span>
                      )}
                    </button>
                  ))}
                </div>
                {options.length === 1 && (
                  <p className="mt-2 text-xs text-slate-500">
                    {t("req.validity_single_option", { label: t(VALIDITY_LABEL_KEYS[options[0]]) })}
                  </p>
                )}
              </>
            );
          })()}
        </Field>
      </Card>
    </div>
  );
}

function StepDocs({
  form,
  isJuridica,
  uploadedDocs,
  validations,
  validatingDocs,
  onPick,
}: {
  form: FormState;
  isJuridica: boolean;
  uploadedDocs: Partial<Record<CustomerDocType, string>>;
  validations: Partial<Record<CustomerDocType, DocValidation | null>>;
  validatingDocs: Partial<Record<CustomerDocType, boolean>>;
  onPick: (
    key: keyof FormState["docs"],
    type: CustomerDocType,
    picked: PickedFile | null
  ) => void;
}) {
  const { t } = useT();
  return (
    <div className="space-y-4">
      <header>
        <h2 className="text-2xl font-semibold">{t("req.step3_title")}</h2>
        <p className="mt-1 text-sm text-slate-600">{t("req.step3_subtitle")}</p>
      </header>
      <div className="grid gap-4 md:grid-cols-2">
        <DocSlot
          label={t("req.doc_cedula")}
          hint={t("req.doc_cedula_hint")}
          value={form.docs.cedula}
          uploadedName={uploadedDocs.cedula}
          loading={validatingDocs.cedula}
          result={validations.cedula}
          onChange={(f) => onPick("cedula", "cedula", f)}
        />
        <DocSlot
          label={t("req.doc_rut")}
          hint={t("req.doc_rut_hint")}
          value={form.docs.rut}
          uploadedName={uploadedDocs.rut}
          optional={!isJuridica}
          loading={validatingDocs.rut}
          result={validations.rut}
          onChange={(f) => onPick("rut", "rut", f)}
        />
        {isJuridica && (
          <DocSlot
            label={t("req.doc_camara")}
            hint={t("req.doc_camara_hint")}
            value={form.docs.camara}
            uploadedName={uploadedDocs.camara_comercio}
            className="md:col-span-2"
            loading={validatingDocs.camara_comercio}
            result={validations.camara_comercio}
            onChange={(f) => onPick("camara", "camara_comercio", f)}
          />
        )}
      </div>
    </div>
  );
}

// A document slot that, on resume, shows the already-uploaded file (whose File
// object is gone) as a confirmation instead of an empty dropzone. Renders the
// advisory AI validation verdict (PC-29) underneath.
function DocSlot({
  label,
  hint,
  value,
  uploadedName,
  optional,
  className,
  loading,
  result,
  onChange,
}: {
  label: string;
  hint: string;
  value: PickedFile | null;
  uploadedName?: string;
  optional?: boolean;
  className?: string;
  loading?: boolean;
  result?: DocValidation | null;
  onChange: (picked: PickedFile | null) => void;
}) {
  return (
    <div className={className}>
      {!value && uploadedName ? (
        <div className="card flex items-center gap-4 p-4">
          <span className="inline-flex h-10 w-10 flex-none items-center justify-center rounded-xl bg-emerald-50 text-emerald-700">
            <CheckCircle2 className="h-5 w-5" />
          </span>
          <div className="min-w-0 flex-1">
            <p className="text-xs font-medium uppercase tracking-wider text-slate-500">{label}</p>
            <p className="truncate text-sm font-medium text-slate-900">{uploadedName}</p>
            <p className="text-xs text-emerald-600">Documento subido</p>
          </div>
          <button type="button" onClick={() => onChange(null)} className="btn-ghost h-9 px-3 text-xs">
            Reemplazar
          </button>
        </div>
      ) : (
        <UploadDropzone label={label} hint={hint} value={value} onChange={onChange} optional={optional} />
      )}
      <DocValidationNote loading={loading} result={result} />
    </div>
  );
}

// Advisory AI verdict shown under a document slot. Never blocks the wizard.
function DocValidationNote({ loading, result }: { loading?: boolean; result?: DocValidation | null }) {
  if (loading) {
    return (
      <p className="mt-2 flex items-center gap-2 text-xs text-slate-500">
        <Loader2 className="h-3.5 w-3.5 animate-spin" />
        Validando documento con IA…
      </p>
    );
  }
  if (!result) return null;

  const issues = result.checks.filter((c) => c.status !== "pass");
  if (result.overall === "pass" && issues.length === 0) {
    return (
      <p className="mt-2 flex items-center gap-2 text-xs font-medium text-emerald-700">
        <CheckCircle2 className="h-3.5 w-3.5" />
        Documento verificado
      </p>
    );
  }

  const fail = result.overall === "fail";
  const shown = issues.length ? issues : result.checks;
  return (
    <div
      className={cn(
        "mt-2 rounded-xl border p-3 text-xs",
        fail
          ? "border-red-200 bg-red-50 text-red-800"
          : "border-amber-200 bg-amber-50 text-amber-800"
      )}
    >
      <p className="flex items-center gap-2 font-semibold">
        <AlertTriangle className="h-3.5 w-3.5 flex-none" />
        {fail ? "Revisa este documento" : "Posible problema con el documento"}
      </p>
      {shown.length > 0 && (
        <ul className="mt-1.5 list-disc space-y-0.5 pl-4">
          {shown.map((c) => (
            <li key={c.id}>{c.detail ?? c.label}</li>
          ))}
        </ul>
      )}
      <p className="mt-1.5 text-[11px] opacity-80">
        Puedes corregirlo y volver a subirlo, o continuar de todas formas.
      </p>
    </div>
  );
}

function StepReview({
  form,
  isJuridica,
  uploadedDocs,
}: {
  form: FormState;
  isJuridica: boolean;
  uploadedDocs: Partial<Record<CustomerDocType, string>>;
}) {
  const { t } = useT();
  return (
    <div className="space-y-4">
      <header>
        <h2 className="text-2xl font-semibold">{t("req.step4_title")}</h2>
        <p className="mt-1 text-sm text-slate-600">{t("req.step4_subtitle")}</p>
      </header>
      <div className="grid gap-4 md:grid-cols-2">
        <Card className="p-6">
          <h3 className="text-sm font-semibold uppercase tracking-wider text-slate-500">
            {t("req.step1_title")}
          </h3>
          {form.type && (
            <div className="mt-4 flex items-center gap-3">
              <CertificateTypeIcon type={form.type} size="md" />
              <div>
                <p className="font-semibold text-slate-900">{t(`type.${form.type}` as const)}</p>
                <p className="text-xs text-slate-500">{t(VALIDITY_LABEL_KEYS[form.validity])}</p>
              </div>
            </div>
          )}
        </Card>

        <Card className="p-6">
          <h3 className="text-sm font-semibold uppercase tracking-wider text-slate-500">
            {t("req.step2_title")}
          </h3>
          <dl className="mt-4 space-y-2 text-sm">
            <Row
              label={form.docType === "NIT" ? t("name.company") : t("req.field_name")}
              value={
                form.docType === "NIT"
                  ? form.name
                  : composeDisplayName(form)
              }
            />
            <Row label={t("req.field_doc_number")} value={`${form.docType} ${form.docNumber}`} />
            <Row label={t("req.field_email")} value={form.email} />
            <Row label={t("req.field_phone")} value={form.phone} />
            <Row label={t("req.field_city")} value={`${form.city}, ${form.department}`} />
          </dl>
        </Card>

        <Card className="border-brand-200 bg-gradient-to-br from-brand-50 to-white p-6 md:col-span-2">
          <h3 className="text-sm font-semibold uppercase tracking-wider text-brand-700">
            {t("req.price_title")}
          </h3>
          <div className="mt-4 flex flex-wrap items-end justify-between gap-3">
            <div>
              <p className="text-3xl font-semibold tracking-tight text-slate-900">
                {form.type ? formatCop(priceForCertificate(form.type, form.validity).cop) : "—"}
              </p>
              <p className="mt-1 text-sm text-slate-600">
                {form.type ? `${t(`type.${form.type}` as const)} · ` : ""}
                {t(VALIDITY_LABEL_KEYS[form.validity])} · {t("req.price_subtitle")}
              </p>
            </div>
            <ul className="space-y-1 text-xs text-slate-600">
              <li className="flex items-center gap-2"><CheckCircle2 className="h-3.5 w-3.5 text-emerald-600" /> {t("req.price_includes_dian")}</li>
              <li className="flex items-center gap-2"><CheckCircle2 className="h-3.5 w-3.5 text-emerald-600" /> {t("req.price_includes_issuer")}</li>
              <li className="flex items-center gap-2"><CheckCircle2 className="h-3.5 w-3.5 text-emerald-600" /> {t("req.price_includes_support")}</li>
            </ul>
          </div>
          <p className="mt-4 rounded-xl border border-amber-200 bg-amber-50 p-3 text-xs leading-relaxed text-amber-900">
            {t("req.approval_conditional")}
          </p>
          <p className="mt-4 inline-flex items-center gap-2 rounded-full bg-white/70 px-3 py-1 text-xs font-medium text-slate-600">
            <Lock className="h-3 w-3" />
            {t("req.price_secure_checkout")}
          </p>
        </Card>

        <Card className="p-6 md:col-span-2">
          <h3 className="text-sm font-semibold uppercase tracking-wider text-slate-500">
            {t("req.step3_title")}
          </h3>
          <ul className="mt-4 grid gap-2 sm:grid-cols-2">
            <DocLine label={t("req.doc_cedula")} value={form.docs.cedula?.name ?? uploadedDocs.cedula} />
            <DocLine
              label={t("req.doc_rut")}
              value={form.docs.rut?.name ?? uploadedDocs.rut}
              optional={!isJuridica}
            />
            {isJuridica && (
              <DocLine
                label={t("req.doc_camara")}
                value={form.docs.camara?.name ?? uploadedDocs.camara_comercio}
              />
            )}
          </ul>
        </Card>
      </div>
    </div>
  );
}

function Field({ label, required, children }: { label: string; required?: boolean; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="label flex items-center gap-2">
        {label}
        {required && <span className="text-xs font-normal text-slate-400">·</span>}
      </span>
      <div className="mt-1.5">{children}</div>
    </label>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-center justify-between gap-4">
      <dt className="text-xs uppercase tracking-wider text-slate-500">{label}</dt>
      <dd className="truncate text-right font-medium text-slate-900">{value}</dd>
    </div>
  );
}

function DocLine({ label, value, optional }: { label: string; value?: string; optional?: boolean }) {
  const { t } = useT();
  return (
    <li className="flex items-center justify-between gap-3 rounded-xl border border-slate-200/80 p-3">
      <span className="truncate text-sm font-medium text-slate-900">{label}</span>
      <span
        className={cn(
          "truncate text-xs",
          value ? "text-emerald-700" : optional ? "text-slate-400" : "text-red-700"
        )}
      >
        {value ?? (optional ? t("common.optional") : t("status.missing"))}
      </span>
    </li>
  );
}

function Success({ reference, email }: { reference: string; email: string }) {
  const { t } = useT();
  return (
    <div className="mx-auto max-w-xl py-16 text-center">
      <div className="mx-auto mb-6 inline-flex h-16 w-16 items-center justify-center rounded-3xl bg-emerald-100 text-emerald-700">
        <CheckCircle2 className="h-9 w-9" />
      </div>
      <h1 className="text-3xl font-semibold tracking-tight">{t("req.success_title")}</h1>
      <p className="mt-3 text-slate-600">{t("req.success_body", { email: email || "tu correo" })}</p>
      <div className="mt-8 inline-flex items-center gap-2 rounded-full bg-slate-100 px-4 py-2 font-mono text-sm text-slate-700">
        <span className="text-xs uppercase tracking-wider text-slate-500">{t("req.success_ref")}:</span>
        <span className="font-semibold text-slate-900">{reference}</span>
      </div>
      <div className="mt-10 flex flex-wrap items-center justify-center gap-3">
        <Link to="/" className="btn-primary">
          {t("req.success_go_dashboard")}
        </Link>
        <Link to="/certificados" className="btn-secondary">
          {t("nav.certificates")}
        </Link>
      </div>
    </div>
  );
}
